// The lead's numbers and which one the dialer calls (owner request, PJ, 2026-10-23: "I want to prioritize owner cells over
// everything, because that is the direct contact to the decision maker. And if they don't pick up, or if we choose to call
// the store, that should be an option." / "For the ones where it's one owner in several stores, just mark this is the
// owner cell and he owns several stores. If we had called that owner cell before, just have like some sort of thing, you
// have called this before."). One helper for the Worker and the web, so queue/next, prepare, /twilio/voice, the outcome
// and the lead card agree on which number is the owner's, which is the store's, and which one Dial calls.
//
// The fields (migration 0028 + 0029, on every Business that lib/rows.ts businessFromRow builds):
//   phone_e164        the business's own number: its identity (UNIQUE; inbound calls and texts match it first), and the
//                     number line type, lookups and the national-DNC fields describe. Never changed by this.
//   owner_phone_e164  the owner's number from the licence, when phone_e164 is the store line (0029; not unique: a multi-store
//                     owner has the same cell on several licences). owner_phone_kind says whether it is likely a cell.
//   phone_kind        0028: 'owner_cell_likely' = phone_e164 itself is probably the owner's cell (BC, Alberta and the US
//                     rows that kept the licence number), with the store's Google-listed line in google_phone_e164.
//                     'unknown' with a Google line that differs (review 2026-10-23: 26 BC and Alberta rows): the licence
//                     number is unclear, so, as the backfill's unclear numbers, it is the owner's line without "likely
//                     cell", and the Google line is the store line.
//   bad_number        phone_e164 was logged wrong and the lead had nothing else (the lead has no working number).
//   phone_wrong_at    0029: phone_e164 was logged wrong while the lead still had another number: only it is blocked.
import type { DispositionCode, GateResult, ISODate } from './api';
import type { PhoneKind } from './api-core';

/** Which of a lead's numbers a dial is for. Absent on a request = the lead's default (leadNumbers().defaultTarget). */
export type DialTarget = 'owner' | 'store';
export const DIAL_TARGETS: readonly DialTarget[] = ['owner', 'store'];
export type OwnerPhoneKind = 'owner_cell_likely' | 'unknown';
export const OWNER_PHONE_KINDS: readonly OwnerPhoneKind[] = ['owner_cell_likely', 'unknown'];

/** The business columns the roles come from (camelCase, as Business carries them; the 0029 ones may be absent). */
export interface LeadNumberFields {
  phoneE164: string;
  ownerPhoneE164?: string | null;
  ownerPhoneKind?: OwnerPhoneKind | null;
  googlePhoneE164?: string | null;
  phoneKind?: PhoneKind | null;
  badNumber?: boolean;
  phoneWrongAt?: string | null;
}

/** One number with a role. `isMain`: it is phone_e164 (so the business's line type / lookup / national-DNC facts are
 *  about it); every other number has none of those facts. `likelyCell`: the owner's number is probably a cell. */
export interface LeadNumber { target: DialTarget; e164: string; likelyCell: boolean; isMain: boolean }

/** The number one dial goes to: a role, or (target null) the lead's one number, which has no role. */
export interface DialledNumber { target: DialTarget | null; e164: string; isMain: boolean; likelyCell: boolean }

export interface LeadNumbers {
  owner: LeadNumber | null;
  store: LeadNumber | null;
  /** What Dial / Space, queue/next, a due callback and Call back now call unless the setter picks the store: the owner's
   *  number when there is one ("prioritize owner cells over everything"); else phone_e164; else (phone_e164 logged wrong)
   *  the store line. null = phone_e164 with no role (a lead with one number: nothing new shows on its card). */
  defaultTarget: DialTarget | null;
  /** The default dial (DialledNumber of defaultTarget). */
  default: DialledNumber;
  /** phone_e164 may be dialled: not logged wrong (bad_number, phone_wrong_at). */
  mainUsable: boolean;
}

/** True when phone_e164 itself is the owner's line with the store's Google line beside it (PJ 2026-10-23): phone_kind
 *  'owner_cell_likely', or (review 2026-10-23) 'unknown' with a Google line that differs: an unclear licence number is
 *  treated as the owner's line, as the backfill's unclear numbers are (A's decision; "do whatever you think is best"). */
export function mainIsOwnerLine(b: Pick<LeadNumberFields, 'phoneE164' | 'phoneKind' | 'googlePhoneE164'>): boolean {
  if (b.phoneKind === 'owner_cell_likely') return true;
  const google = clean(b.googlePhoneE164);
  return b.phoneKind === 'unknown' && !!google && google !== b.phoneE164;
}

/**
 * Every lead's numbers, by rule (the orchestrator's brief of 2026-10-23, from PJ's words):
 *   owner = owner_phone_e164 when set; else phone_e164 when it is the owner's line (mainIsOwnerLine: phone_kind
 *           'owner_cell_likely', or 'unknown' with a different Google line; not logged wrong);
 *   store = phone_e164 when owner_phone_e164 is set and differs (and phone_e164 is not logged wrong); else the Google line
 *           when phone_e164 is the owner's line and it differs from phone_e164;
 *   default = the owner's number when there is one; else phone_e164 (or, logged wrong, the store line). Pure.
 */
export function leadNumbers(b: LeadNumberFields): LeadNumbers {
  const main = b.phoneE164;
  const mainUsable = b.badNumber !== true && !b.phoneWrongAt;
  const ownerCol = clean(b.ownerPhoneE164);
  const google = clean(b.googlePhoneE164);
  const mainOwner = mainIsOwnerLine(b);
  let owner: LeadNumber | null = null;
  if (ownerCol && (ownerCol !== main || mainUsable)) {
    owner = { target: 'owner', e164: ownerCol, likelyCell: b.ownerPhoneKind === 'owner_cell_likely', isMain: ownerCol === main };
  } else if (!ownerCol && mainOwner && mainUsable) {
    owner = { target: 'owner', e164: main, likelyCell: b.phoneKind === 'owner_cell_likely', isMain: true };
  }
  let store: LeadNumber | null = null;
  if (ownerCol && ownerCol !== main) {
    if (mainUsable) store = { target: 'store', e164: main, likelyCell: false, isMain: true };
  } else if (mainOwner && google && google !== main && google !== owner?.e164) {
    store = { target: 'store', e164: google, likelyCell: false, isMain: false };
  }
  const roleless: DialledNumber = { target: null, e164: main, isMain: true, likelyCell: false };
  const pick = owner ?? (mainUsable ? null : store);
  return {
    owner, store, mainUsable,
    defaultTarget: pick ? pick.target : null,
    default: pick ? { ...pick } : roleless,
  };
}

function clean(v: string | null | undefined): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : null;
}

/** The number a dial with `target` goes to: that role's number (null when the lead has no such number), or, target
 *  absent, the default. */
export function dialNumberFor(nums: LeadNumbers, target?: DialTarget | null): DialledNumber | null {
  if (target === undefined || target === null) return nums.default;
  const n = target === 'owner' ? nums.owner : nums.store;
  return n ? { ...n } : null;
}

/** Which of the lead's numbers `e164` is (a placed call's to_e164): its role, the roleless phone_e164 (target null), or
 *  null when it is none of this lead's numbers (/twilio/voice refuses such a dial: number_mismatch). */
export function dialledNumberOf(nums: LeadNumbers, b: Pick<LeadNumberFields, 'phoneE164'>, e164: string | null | undefined): DialledNumber | null {
  if (!e164) return null;
  if (nums.owner && nums.owner.e164 === e164) return { ...nums.owner };
  if (nums.store && nums.store.e164 === e164) return { ...nums.store };
  if (e164 === b.phoneE164) return { target: null, e164, isMain: true, likelyCell: false };
  return null;
}

/**
 * "Wrong number" on a call (PJ 2026-10-23: only the number dialled is wrong, not the lead). The dialled number is taken
 * off this business in every role it holds: owner_phone_e164 cleared, google_phone_e164 cleared, and phone_e164 (which
 * can't be cleared: it is the business's identity) marked wrong: `phone_wrong_at` while the lead still has another
 * number, `bad_number` (the lead has no working number, as before 0029) when it doesn't. When nothing usable is left after
 * an owner or Google number is cleared (phone_e164 was already logged wrong), bad_number is set too. `keepsNumber`: the
 * lead stays callable on a number it has left, so the lead isn't closed as bad data. A number that is none of the
 * business's (an old call to a number since changed) changes nothing. Review 2026-10-23: a column holding phone_e164
 * itself (google_phone_e164 on every 'store' row, which says what Google lists) is never cleared: marking phone_e164 is
 * enough, and the 10-minute correction (lifecycle.ts) could not put such a column back. Pure.
 */
export function wrongNumberPlan(b: LeadNumberFields, wrongE164: string): {
  clearOwner: boolean; clearGoogle: boolean; markMain: 'phone_wrong' | 'bad_number' | null; keepsNumber: boolean;
} {
  const isMain = b.phoneE164 === wrongE164;
  const clearOwner = !isMain && clean(b.ownerPhoneE164) === wrongE164;
  const clearGoogle = !isMain && clean(b.googlePhoneE164) === wrongE164;
  if (!clearOwner && !clearGoogle && !isMain) {
    // Not one of its numbers now (already cleared by an earlier save of this outcome, or changed): nothing to clear, and
    // the lead stays as it is (open when it has a working number, closed when it doesn't).
    const now = leadNumbers(b);
    const keeps = !!(now.owner || now.store || now.mainUsable);
    return { clearOwner, clearGoogle, markMain: keeps ? null : 'bad_number', keepsNumber: keeps };
  }
  const after = leadNumbers({
    ...b,
    ownerPhoneE164: clearOwner ? null : b.ownerPhoneE164,
    googlePhoneE164: clearGoogle ? null : b.googlePhoneE164,
    phoneWrongAt: isMain ? (b.phoneWrongAt || 'wrong') : b.phoneWrongAt,
  });
  const keepsNumber = !!(after.owner || after.store || after.mainUsable);
  const markMain = isMain ? (keepsNumber ? 'phone_wrong' : 'bad_number') : keepsNumber ? null : 'bad_number';
  return { clearOwner, clearGoogle, markMain, keepsNumber };
}

/** "Owner's line", "Owner's line (likely cell)", "Store line". */
export function numberLabel(n: { target: DialTarget; likelyCell?: boolean }): string {
  return n.target === 'owner' ? (n.likelyCell ? "Owner's line (likely cell)" : "Owner's line") : 'Store line';
}

/** A call's number in the call bar and history: "Owner's line" / "Store line" (no "likely cell" there). */
export const NUMBER_ROLE_WORDS: Record<DialTarget, string> = { owner: "Owner's line", store: 'Store line' };

/** The other number: the store line after the owner's, the owner's after the store's. */
export function otherTarget(t: DialTarget): DialTarget {
  return t === 'owner' ? 'store' : 'owner';
}

/** Review 2026-10-23: the person on this number may be somewhere other than the business, so the calling rules of the
 *  state or province its area code is in apply too (calling turned off, unreviewed, hours, Sundays, holidays), and its
 *  zones are never settled by a confirmed store zone: the owner's line (owner_phone_e164, or phone_e164 when it is the
 *  owner's line) and any number other than phone_e164 (the store's Google line). The business's own line keeps the
 *  business's state alone, as before. Pure. */
export function numberMayBeElsewhere(d: Pick<DialledNumber, 'target' | 'isMain'> | null | undefined): boolean {
  return !!d && (d.target === 'owner' || !d.isMain);
}

// ---------- what the lead card gets (QueueItem.numbers) ----------

/** "You've called this number before: 2 calls, last Oct 21 by Max (No answer)": the placed outbound calls to one number,
 *  any lead, any campaign or none. lastBy: the caller's first name ("Valistix" for a caller whose numbers are hidden from
 *  the viewer); lastOutcome null = not logged yet. Never another business's notes. */
export interface CalledBefore { calls: number; lastAt: ISODate; lastBy: string | null; lastOutcome: DispositionCode | null }

/** One of the lead's numbers on the card, with the dial gate for calling it now (the default's is QueueItem.dialGate too). */
export interface QueueItemNumber {
  target: DialTarget;
  e164: string;
  likelyCell: boolean;
  gate: GateResult;
  /** The voicemail-drop gate as if a call to this number were answered (QueueItem.dropGate is the default number's). */
  dropGate: GateResult;
  calledBefore: CalledBefore | null;
}

/** A business that has the same owner's number, for "This owner has 3 stores in our lists: ...". Name and city only. */
export interface OwnerStore { name: string; city: string | null; thisLead: boolean }

/** The owner's stores on the card. `basis` (review 2026-10-23; absent = 'owner_number'): 'owner_number' = businesses in our
 *  lists holding the same owner's number (certain); 'licence_group' = the licence list grouped the stores under one owner
 *  (the lead's note "1 of 3 stores (Cloud 620): same owner?", LICENCE_GROUP_RE), with different numbers: the lists merge
 *  every licence that shares a phone into one lead, so a multi-store owner's stores almost always carry different numbers
 *  and the same-number match finds none. `listed`: how many stores the licence list counts in the group (some are not in
 *  our lists: merged as the same phone, a chain, or closed). `total`: how many are in our lists. */
export interface OwnerStores { total: number; stores: OwnerStore[]; basis?: 'owner_number' | 'licence_group'; listed?: number }

/** QueueItem.numbers (PJ 2026-10-23). Absent from an older server: the card then shows phone_e164 as before. */
export interface QueueItemNumbers {
  defaultTarget: DialTarget | null;
  owner: QueueItemNumber | null;
  store: QueueItemNumber | null;
  /** The default number's called-before line (also on its entry when it has a role). */
  calledBefore: CalledBefore | null;
  /** The owner's stores, this one first (capped at OWNER_STORES_MAX): the businesses with the same owner's number when
   *  there are 2 or more; else (review 2026-10-23) the licence list's group of 2 or more stores (OwnerStores.basis). */
  ownerStores: OwnerStores | null;
}

export const OWNER_STORES_MAX = 6;

/** The lead-list note the dispensary pipeline writes on a store the licence list groups under one owner (dm_role, e.g.
 *  "Store manager (AGLC list); 1 of 3 stores (Cloud 620): same owner?"). Groups: 1 = how many stores, 2 = the group's
 *  name. Every store of a group carries the same "of 3 stores (Cloud 620)". */
export const LICENCE_GROUP_RE = /\b\d+ of (\d+) stores \(([^()]{1,120})\): same owner\?/;

/** The licence-list group of a lead's note, or null. */
export function licenceGroupOf(dmRole: string | null | undefined): { listed: number; name: string } | null {
  const m = typeof dmRole === 'string' ? LICENCE_GROUP_RE.exec(dmRole) : null;
  if (!m) return null;
  const listed = Number(m[1]);
  return Number.isInteger(listed) && listed >= 2 ? { listed, name: m[2] } : null;
}
